/**
 * Player network diagnostics — shared across WebRTC, MSE, Shoutcast, V2, Demo.
 *
 * Reuses getStreamNetworkDiagnostics (navigator.connection / resource timing /
 * vendor proxy detection) and layers a `likelyCause` heuristic classifier on
 * top. The goal: when a player fails, the Sentry event should tell us enough
 * about the user's network to route the fix (firewall, corporate proxy,
 * offline, autoplay blocked, etc.).
 */

import {
  getStreamNetworkDiagnostics,
  type StreamNetworkDiagnostics,
} from "@/shared/services/configurable-sse-client/networkDiagnostics";

export type PlayerConnectionType = "webrtc" | "mse" | "shoutcast" | "v2" | "demo";

export type PlayerLikelyCause =
  | "offline"
  | "corporate_proxy"
  | "slow_network"
  | "autoplay_blocked"
  | "stun_turn_blocked"
  | "stun_blocked"
  | "websocket_blocked"
  | "websocket_blocked_or_auth"
  | "audio_context_blocked"
  | "unknown";

export interface PlayerFailureDiagnostics {
  network: StreamNetworkDiagnostics;
  likelyCause: PlayerLikelyCause;
}

interface MediaSnapshot {
  networkState?: string;
  readyState?: string;
}

/** Classify a player failure into a likely cause based on error text + network state. */
function classifyLikelyCause(
  connectionType: PlayerConnectionType,
  errorMessage: string,
  network: StreamNetworkDiagnostics,
  mediaSnapshot?: MediaSnapshot
): PlayerLikelyCause {
  const msg = errorMessage.toLowerCase();

  // 1. Offline trumps everything
  if (!network.online) return "offline";

  // 2. Vendor proxy headers (Zscaler/Bluecoat/Fortinet/etc) present = strong proxy signal
  if (network.vendorProxyHeaders && Object.keys(network.vendorProxyHeaders).length > 0) {
    return "corporate_proxy";
  }

  // 3. Browser autoplay policy
  if (msg.includes("notallowederror") || msg.includes("autoplay") || msg.includes("not allowed")) {
    return "autoplay_blocked";
  }

  // 4. AudioContext security/support
  if (msg.includes("audiocontext") || msg.includes("securityerror") || msg.includes("notsupportederror")) {
    return "audio_context_blocked";
  }

  // 5. WebRTC ICE failures
  if (connectionType === "webrtc") {
    if (msg.includes("ice_connection_failed") || msg.includes("ice_connection_closed")) {
      return "stun_turn_blocked";
    }
    if (msg.includes("set_remote_description_failed")) {
      return "stun_blocked";
    }
  }

  // 6. WebSocket-level failures (affects MSE, V2)
  if (msg.includes("ws_closed") || msg.includes("ws_error") || msg.includes("websocket")) {
    return "websocket_blocked";
  }

  // 7. MSE connected but received no data
  if (
    mediaSnapshot?.networkState === "NETWORK_LOADING" &&
    mediaSnapshot?.readyState === "HAVE_NOTHING"
  ) {
    return "websocket_blocked_or_auth";
  }

  // 8. Slow network (final fallback signal)
  const slowTypes = new Set(["slow-2g", "2g", "3g"]);
  if (
    (network.connectionType && slowTypes.has(network.connectionType)) ||
    (typeof network.rtt === "number" && network.rtt > 1000)
  ) {
    return "slow_network";
  }

  return "unknown";
}

/**
 * Capture network diagnostics + classify the likely cause of a player failure.
 * Safe to call from any player code path; best-effort — never throws.
 */
export function getPlayerFailureDiagnostics(
  address: string,
  connectionType: PlayerConnectionType,
  errorMessage: string,
  mediaSnapshot?: MediaSnapshot
): PlayerFailureDiagnostics {
  let network: StreamNetworkDiagnostics;
  try {
    // getStreamNetworkDiagnostics keys its header cache by connection name;
    // players don't populate that cache, so the headers fields will be absent —
    // we still get navigator / resource-timing which is the bulk of the value.
    network = getStreamNetworkDiagnostics(`player:${connectionType}`, address);
  } catch {
    network = {
      online: typeof navigator !== "undefined" ? navigator.onLine : true,
      hasServiceWorker: false,
    };
  }

  const likelyCause = classifyLikelyCause(
    connectionType,
    errorMessage,
    network,
    mediaSnapshot
  );

  return { network, likelyCause };
}

/**
 * Build the standard Sentry tags block for a player failure. Pairs with
 * `extra: { networkDiagnostics: network }` at the call site.
 */
export function buildPlayerFailureTags(
  component: string,
  context: string,
  connectionType: PlayerConnectionType,
  diagnostics: PlayerFailureDiagnostics
): Record<string, string> {
  return {
    component,
    context,
    connection_type: connectionType,
    "network.online": String(diagnostics.network.online),
    "network.connectionType": diagnostics.network.connectionType ?? "unknown",
    "network.proxyDetected": String(
      !!diagnostics.network.vendorProxyHeaders &&
        Object.keys(diagnostics.network.vendorProxyHeaders).length > 0
    ),
    "player.likelyCause": diagnostics.likelyCause,
  };
}
